// src/features/resources/ResourceManagePanel.tsx
// Fuller link-management surface for the current rail entity: direction
// filter (incoming/outgoing/both), aggregate stats, per-link relation
// editing and unlink. Opened from ResourceList's "Manage" affordance —
// the compact grouped list in the rail stays the fast path for daily use;
// this panel is for the occasional deeper look.

import { useEffect, useState, useCallback } from 'react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { Select } from '@/components/Select';
import { resourceStore } from '@core/stores/resource/ResourceStore';
import { listLinksByDirection, unlinkResources } from './api';
import { errMessage } from './format';
import { RELATION_TYPE_CONFIG } from './logic';
import type { LinkDirection, LinkStatsResponse, ResourceLink, Resource } from '@core/types/resource';

interface EntityContext {
  urn: string;
  type: string;
  id: string;
  title: string;
}

export interface ResourceManagePanelProps {
  entity: EntityContext;
  onClose: () => void;
  /** Called after a link is edited or removed so the caller can refresh its own list. */
  onChanged: () => void;
}

const ALL_RELATION_TYPES = Object.keys(RELATION_TYPE_CONFIG);

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

export function ResourceManagePanel({ entity, onClose, onChanged }: ResourceManagePanelProps) {
  const { t } = useI18n();
  const [direction, setDirection] = useState<LinkDirection>('LINK_DIRECTION_BOTH');
  const [links, setLinks] = useState<ResourceLink[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [stats, setStats] = useState<LinkStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRelationType, setEditRelationType] = useState('');
  const [editNote, setEditNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(() => {
    setIsLoading(true);
    setError(null);
    Promise.all([
      listLinksByDirection(entity.type, entity.id, direction, true),
      resourceStore.GetLinkStats(entity.type, entity.id),
    ])
      .then(([linksRes, statsRes]) => {
        setLinks(linksRes.links?.items ?? []);
        setResources(linksRes.resources?.items ?? []);
        setStats(statsRes);
      })
      .catch((e) => setError(errMessage(e, t('rail.resources.manageLoadError'))))
      .finally(() => setIsLoading(false));
  }, [entity.type, entity.id, direction, t]);

  useEffect(() => { load(); }, [load]);

  const resourceForUrn = (urn: string) => resources.find((r) => r.urn === urn);

  const startEdit = (link: ResourceLink) => {
    setEditingId(link.entity?.id ?? null);
    setEditRelationType(link.relation_type ?? 'RELATION_TYPE_RELATED');
    setEditNote(link.note ?? '');
  };

  const saveEdit = async (link: ResourceLink) => {
    const linkId = link.entity?.id;
    if (!linkId) return;
    setIsSaving(true);
    try {
      await resourceStore.UpdateResourceLink(linkId, {
        link_id: linkId,
        relation_type: editRelationType as any,
        note: editNote,
      });
      setEditingId(null);
      load();
      onChanged();
    } catch (e) {
      setError(errMessage(e, t('rail.resources.manageSaveError')));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnlink = async (link: ResourceLink) => {
    if (!link.source || !link.target) return;
    try {
      await unlinkResources(link.source, link.target, link.relation_type);
      setLinks((prev) => prev.filter((l) => l.entity?.id !== link.entity?.id));
      onChanged();
    } catch (e) {
      setError(errMessage(e, t('rail.resources.manageUnlinkError')));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div role="dialog" aria-modal="true" className="relative mx-4 flex w-full max-w-2xl flex-col rounded-lg border border-border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">{t('rail.resources.manageTitle', { title: entity.title })}</h3>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={t('common.close')}>
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        {stats && (
          <div className="flex items-center gap-4 border-b border-border px-4 py-2 text-xs text-muted-foreground">
            <span>{t('rail.resources.statsIncoming', { count: stats.total_incoming })}</span>
            <span>{t('rail.resources.statsOutgoing', { count: stats.total_outgoing })}</span>
          </div>
        )}

        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          {(['LINK_DIRECTION_BOTH', 'LINK_DIRECTION_INCOMING', 'LINK_DIRECTION_OUTGOING'] as LinkDirection[]).map((d) => (
            <button
              key={d}
              onClick={() => setDirection(d)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                direction === d ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              {t(`rail.resources.direction.${d}`)}
            </button>
          ))}
        </div>

        <div className="max-h-96 overflow-y-auto p-2">
          {error && (
            <div className="mx-2 mb-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          {isLoading ? (
            <p className="px-2 py-4 text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : links.length === 0 ? (
            <p className="px-2 py-4 text-sm text-muted-foreground">{t('rail.resources.manageEmpty')}</p>
          ) : (
            <ul className="space-y-1">
              {links.map((link) => {
                const linkId = link.entity?.id ?? '';
                const otherUrn = link.source_urn === entity.urn ? link.target_urn : link.source_urn;
                const other = resourceForUrn(otherUrn);
                const isEditing = editingId === linkId;
                return (
                  <li key={linkId} className="rounded-md border border-border px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-foreground">{other?.title || otherUrn}</p>
                        <p className="text-xs text-muted-foreground">
                          {link.source_urn === entity.urn ? t('rail.resources.direction.LINK_DIRECTION_OUTGOING') : t('rail.resources.direction.LINK_DIRECTION_INCOMING')}
                          {' · '}
                          {t(RELATION_TYPE_CONFIG[link.relation_type ?? '']?.labelKey ?? 'rail.resources.relationType.related')}
                        </p>
                      </div>
                      {!isEditing && (
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => startEdit(link)}>{t('common.edit')}</Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleUnlink(link)}>{t('rail.resources.unlink')}</Button>
                        </div>
                      )}
                    </div>
                    {isEditing && (
                      <div className="mt-2 space-y-2">
                        <Select
                          value={editRelationType}
                          onChange={(e: any) => setEditRelationType(e.target ? e.target.value : e)}
                          options={ALL_RELATION_TYPES.map((rt) => ({ value: rt, label: t(RELATION_TYPE_CONFIG[rt].labelKey) }))}
                        />
                        <input
                          value={editNote}
                          onChange={(e) => setEditNote(e.target.value)}
                          placeholder={t('rail.resources.notePlaceholder')}
                          className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditingId(null)} className="rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted">
                            {t('common.cancel')}
                          </button>
                          <Button size="sm" onClick={() => saveEdit(link)} isLoading={isSaving}>{t('common.save')}</Button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
